import {
  LayoutDashboard,
  Clipboard,
  BarChart3,
  Settings,
  Menu,
  X,
  LogOut,
  ChevronDown,
  ChevronRight,
  Building2,
  User,
  FolderTree,
  UserPlus,
  BookOpen,
} from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';

import {
  requestSsoExchangeCode,
  buildSsoRedirectUrl,
  type SwitchableModule,
} from '@aia/platform-core';
import { AppSwitcher } from '@aia/ui-components';

import { Breadcrumbs } from '@/components/ui/breadcrumbs';
import { Button } from '@/components/ui/button';
import { useBreadcrumbs } from '@/hooks/useBreadcrumbs';
import { useModules } from '@/hooks/useModules';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';

const APP_URLS: Partial<Record<SwitchableModule, string | undefined>> = {
  pilot: import.meta.env.VITE_PILOT_URL,
  progress: import.meta.env.VITE_PROGRESS_URL,
  insight: import.meta.env.VITE_INSIGHT_URL,
  admin: import.meta.env.VITE_ADMIN_URL,
};

async function switchToApp(target: SwitchableModule): Promise<void> {
  const base = APP_URLS[target];
  if (!base) return;
  const code = await requestSsoExchangeCode(api, target);
  window.location.href = buildSsoRedirectUrl(base, code);
}

export function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [orgMenuOpen, setOrgMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const modules = useModules();
  const breadcrumbs = useBreadcrumbs();
  const mainRef = useRef<HTMLElement>(null);

  useEffect(() => {
    mainRef.current?.focus({ preventScroll: true });
  }, [location.pathname]);

  const mainNavigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, module: null },
    ...(modules.pilotPulse
      ? [
          {
            name: 'Pilots',
            href: '/dashboard/pilots',
            icon: Clipboard,
            module: 'pilotPulse' as const,
          },
        ]
      : []),
    { name: 'Reports', href: '/dashboard/reports', icon: BarChart3, module: null },
    { name: 'Guide', href: '/dashboard/guide', icon: BookOpen, module: null },
    { name: 'Settings', href: '/dashboard/settings', icon: Settings, module: null },
  ];

  // Organization submenu for OrgAdmin/UnitAdmin
  const hasOrgMenu = user?.role && ['OrgAdmin', 'UnitAdmin', 'PlatformAdmin'].includes(user.role);
  const orgSubMenu = [
    { name: 'Structure', href: '/dashboard/organization/structure', icon: FolderTree },
    { name: 'Team', href: '/dashboard/organization/team', icon: UserPlus },
  ];

  // Check if org menu should be expanded (if we're on an org route)
  const isOrgActive = location.pathname.startsWith('/dashboard/organization');

  const isActive = (href: string) => {
    if (href === '/dashboard') {
      return location.pathname === href;
    }
    return location.pathname.startsWith(href);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleProfileClick = () => {
    setProfileMenuOpen(false);
    navigate('/dashboard/settings');
  };

  // Get user initials
  const getUserInitials = () => {
    if (!user?.name) return 'U';
    const parts = user.name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0]?.[0] ?? ''}${parts[parts.length - 1]?.[0] ?? ''}`.toUpperCase();
    }
    return user.name.substring(0, 2).toUpperCase();
  };

  // Get role display name
  const getRoleDisplayName = () => {
    if (!user) return '';
    switch (user.role) {
      case 'PlatformAdmin':
        return 'Platform Admin';
      case 'OrgAdmin':
        return 'Administrator';
      case 'UnitAdmin':
        return 'Unit Admin';
      case 'Instructor':
        return 'Instructor';
      default:
        return user.role;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-[100] focus:p-2 focus:bg-white focus:text-black focus:shadow-lg"
      >
        Skip to main content
      </a>

      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          role="button"
          tabIndex={0}
          aria-label="Close navigation menu"
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setSidebarOpen(false);
            }
          }}
        />
      )}

      {profileMenuOpen && (
        <div
          role="button"
          tabIndex={0}
          aria-label="Close profile menu"
          className="fixed inset-0 z-40"
          onClick={() => setProfileMenuOpen(false)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setProfileMenuOpen(false);
            }
          }}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 bg-white border-r transform transition-transform duration-200 lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
        )}
        aria-label="Main navigation"
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b">
          <Link to="/dashboard" className="flex items-center gap-2">
            <img src="/brand/logo-color.png" alt="Academics in Action" className="h-8" />
          </Link>
          <button
            className="lg:hidden min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-aia-blue"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close navigation menu"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="mt-4 px-2" aria-label="Sidebar">
          <ul className="space-y-1">
            {mainNavigation.map((item) => (
              <li key={item.name}>
                <Link
                  to={item.href}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                    isActive(item.href)
                      ? 'bg-aia-gold/10 text-aia-navy'
                      : 'text-gray-600 hover:bg-gray-100',
                  )}
                  aria-current={isActive(item.href) ? 'page' : undefined}
                  data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <item.icon
                    className={cn(
                      'h-5 w-5',
                      isActive(item.href) ? 'text-aia-gold' : 'text-gray-500',
                    )}
                    aria-hidden="true"
                  />
                  {item.name}
                </Link>
              </li>
            ))}

            {/* Organization submenu for admins */}
            {hasOrgMenu && (
              <li>
                <button
                  onClick={() => setOrgMenuOpen(!orgMenuOpen)}
                  className={cn(
                    'w-full flex items-center justify-between gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                    isOrgActive
                      ? 'bg-aia-gold/10 text-aia-navy'
                      : 'text-gray-600 hover:bg-gray-100',
                  )}
                >
                  <span className="flex items-center gap-3">
                    <Building2
                      className={cn('h-5 w-5', isOrgActive ? 'text-aia-gold' : 'text-gray-500')}
                    />
                    Organization
                  </span>
                  {orgMenuOpen || isOrgActive ? (
                    <ChevronDown className="h-4 w-4 text-gray-500" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-gray-500" />
                  )}
                </button>

                {(orgMenuOpen || isOrgActive) && (
                  <ul className="mt-1 ml-4 space-y-1">
                    {orgSubMenu.map((subItem) => (
                      <li key={subItem.name}>
                        <Link
                          to={subItem.href}
                          className={cn(
                            'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                            isActive(subItem.href)
                              ? 'bg-aia-gold/10 text-aia-navy'
                              : 'text-gray-500 hover:bg-gray-100',
                          )}
                        >
                          <subItem.icon
                            className={cn(
                              'h-4 w-4',
                              isActive(subItem.href) ? 'text-aia-gold' : 'text-gray-500',
                            )}
                          />
                          {subItem.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            )}
          </ul>
        </nav>

        {/* User menu */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t bg-white">
          <div className="relative">
            <button
              className="flex items-center gap-3 w-full p-2 rounded-lg hover:bg-gray-100 transition-colors"
              onClick={() => setProfileMenuOpen(!profileMenuOpen)}
              aria-expanded={profileMenuOpen}
              aria-haspopup="true"
              aria-label="User profile menu"
              data-testid="user-menu"
            >
              <div className="w-8 h-8 rounded-full bg-aia-blue flex items-center justify-center text-white text-sm font-medium">
                {getUserInitials()}
              </div>
              <div className="flex-1 text-left">
                <div className="text-sm font-medium truncate">{user?.name || 'User'}</div>
                <div className="text-xs text-gray-500">{getRoleDisplayName()}</div>
              </div>
              <ChevronDown
                className={cn(
                  'h-4 w-4 text-gray-500 transition-transform',
                  profileMenuOpen && 'rotate-180',
                )}
              />
            </button>

            {/* Profile dropdown menu */}
            {profileMenuOpen && (
              <div className="absolute bottom-full left-0 right-0 mb-2 bg-white border rounded-lg shadow-lg py-1 z-50">
                <div className="px-3 py-2 border-b">
                  <div className="text-sm font-medium">{user?.name}</div>
                  <div className="text-xs text-gray-500">{user?.email}</div>
                </div>
                <button
                  onClick={handleProfileClick}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-left hover:bg-gray-100 transition-colors"
                  aria-label="User profile"
                  data-testid="profile-button"
                >
                  <User className="h-4 w-4 text-gray-500" />
                  Profile & Settings
                </button>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-left text-red-600 hover:bg-red-50 transition-colors"
                  aria-label="Log out"
                  data-testid="logout-button"
                >
                  <LogOut className="h-4 w-4" />
                  Log Out
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main content area */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="h-16 bg-white border-b flex items-center justify-between px-4">
          <button
            className="lg:hidden min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-aia-blue"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open navigation menu"
            aria-expanded={sidebarOpen}
          >
            <Menu className="h-6 w-6" aria-hidden="true" />
          </button>

          <div className="flex items-center gap-4 ml-auto">
            <AppSwitcher
              currentModule="pilot"
              enabledModules={[
                modules.pilotPulse ? 'pilot' : null,
                modules.progressPulse ? 'progress' : null,
                modules.insightPulse ? 'insight' : null,
              ].filter((m): m is string => m !== null)}
              isPlatformAdmin={user?.isPlatformAdmin}
              appUrls={APP_URLS}
              onSwitch={switchToApp}
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              aria-label="Log out"
              data-testid="header-logout-button"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Log Out
            </Button>
          </div>
        </header>

        {/* Breadcrumbs */}
        {breadcrumbs.length > 1 && (
          <div className="px-4 lg:px-6 pt-3 pb-0 bg-white border-b" data-print-hide>
            <Breadcrumbs items={breadcrumbs} />
          </div>
        )}

        {/* Page content */}
        <main
          id="main-content"
          ref={mainRef}
          tabIndex={-1}
          className="p-4 lg:p-6 min-h-[calc(100vh-8rem)] outline-none"
        >
          <Outlet />
        </main>

        {/* Footer */}
        <footer className="py-4 px-6 border-t bg-white text-center text-sm text-gray-500">
          &copy; {new Date().getFullYear()} Academics in Action LLC. All rights reserved.
        </footer>
      </div>
    </div>
  );
}
